import Modal from 'components/Modal';
import appointmentApis from 'features/appointment/services/apis';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import styled from 'styled-components';
import { buildTimeGroups, ITimeGroup } from './time-slots';

type IModalTimePickerProps = {};
type IOpenConfigs = {
  staffId: string;
  date: string;
  defaultValue?: string;
  // When true, the empty state offers re-picking the staff (add-staff flow only).
  allowChangeStaff?: boolean;
};
export type ITimePickResult =
  | { type: 'select'; value: string } // value = 'HH:mm'
  | { type: 'changeStaff' }
  | { type: 'cancel' };
type IModalTimePickerRef = {
  open: (configs: IOpenConfigs) => Promise<ITimePickResult>;
  close: () => void;
};
export const ModalTimePickerRef = React.createRef<IModalTimePickerRef>();
const ModalTimePicker = forwardRef<IModalTimePickerRef, IModalTimePickerProps>((props, ref) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string>('');
  const [groups, setGroups] = useState<ITimeGroup[]>([]);
  const [allowChangeStaff, setAllowChangeStaff] = useState(false);
  const callbackResolve = useRef<(r: ITimePickResult) => void>(() => undefined);

  const isEmpty = !loading && !groups.some((g) => g.slots.length);

  // Resolve the pending open() promise exactly once, then disarm the resolver.
  const settle = (result: ITimePickResult) => {
    const resolve = callbackResolve.current;
    callbackResolve.current = () => undefined;
    resolve(result);
  };

  const handleOpen: IModalTimePickerRef['open'] = (configs) => {
    setSelected(configs.defaultValue || '');
    setGroups([]);
    setAllowChangeStaff(!!configs.allowChangeStaff);
    setOpen(true);
    fetchTimeslots(configs.staffId, configs.date);
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose: IModalTimePickerRef['close'] = () => {
    setOpen(false);
    settle(isEmpty && allowChangeStaff ? { type: 'changeStaff' } : { type: 'cancel' });
  };

  const handleChangeStaff = () => {
    setOpen(false);
    settle({ type: 'changeStaff' });
  };

  const fetchTimeslots = async (staffId: string, date: string) => {
    setLoading(true);
    try {
      const resData: IResponseDataBody<string[]> = await appointmentApis.getStaffTimeSlot(staffId || '', date || '');
      setGroups(buildTimeGroups(resData.data.data || []));
    } catch (error) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selected) return;
    settle({ type: 'select', value: selected });
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal
      visible={open}
      width={680}
      onClose={handleClose}
      hiddenHeader
      hiddenFooter
      noneBodyStyle
      destroyOnClose
    >
      <Container>
        <Header>
          <span>START TIME</span>
        </Header>
        <Content>
          {loading && <EmptyText>Loading...</EmptyText>}
          {isEmpty && (
            <EmptyText>
              {allowChangeStaff ? 'No available time for this staff. Please choose another staff.' : 'No available time'}
            </EmptyText>
          )}
          {groups
            .filter((group) => group.slots.length > 0)
            .map((group) => (
              <Section key={group.title}>
                <SectionTitle>{group.title}</SectionTitle>
                <TimeGrid>
                  {group.slots.map((slot) => (
                    <TimeCell
                      key={`${group.title}-${slot.value}`}
                      type="button"
                      data-active={slot.value === selected}
                      onClick={() => setSelected(slot.value)}
                    >
                      {slot.label}
                    </TimeCell>
                  ))}
                </TimeGrid>
              </Section>
            ))}
        </Content>
        <Footer>
          {isEmpty && allowChangeStaff ? (
            <button type="button" className="btn btn-submit" onClick={handleChangeStaff}>
              <span>Choose Another Staff</span>
            </button>
          ) : (
            <>
              <button type="button" className="btn" onClick={handleClose}>
                <span>Close</span>
              </button>
              <button type="button" className="btn btn-submit" disabled={!selected} onClick={handleConfirm}>
                <span>OK</span>
              </button>
            </>
          )}
        </Footer>
      </Container>
    </Modal>
  );
});

ModalTimePicker.displayName = 'ModalTimePicker';

export default ModalTimePicker;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: #232f3e;
  span {
    flex: 1;
    color: #fff;
    text-align: center;
    font-family: Poppins;
    font-size: 24px;
    font-weight: 600;
    text-transform: uppercase;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 16px 40px;
`;

const EmptyText = styled.div`
  padding: 24px 0;
  text-align: center;
  color: #64748b;
  font-family: Poppins;
  font-size: 18px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  color: #1d2129;
  font-family: Poppins;
  font-size: 20px;
  font-weight: 600;
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
`;

const TimeCell = styled.button`
  display: flex;
  height: 64px;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  border: 1px solid #ccd4dc;
  border-radius: 4px;
  background: #fff;
  color: #1d2129;
  font-family: Poppins;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &[data-active='true'] {
    background: #2d6772;
    border-color: #2d6772;
    color: #fff;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px 16px 24px;
  background: #fff;
  box-shadow: 0 -4px 4px rgba(0, 0, 0, 0.15);
  .btn {
    display: flex;
    flex: 1 0 0;
    height: 56px;
    padding: 16px 24px;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    background: #e5e6eb;
    box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
      0 -3px 0 0 #d6d6e7 inset;
    cursor: pointer;
    span {
      color: #1d2129;
      text-align: center;
      text-transform: uppercase;
      font-family: Poppins;
      font-size: 20px;
      font-weight: 600;
    }
    &.btn-submit {
      background: #ff8890;
      box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3),
        0 -4px 0 0 #e7646d inset;
      span {
        color: #fff;
      }
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;
